import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useAction, useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { useCallback, useEffect, useState } from "react";
import { api } from "@constructionautomation/backend";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserProfile } from "@/components/auth";

type InvitationState = "pending" | "accepted" | "expired" | "revoked";

type InvitationRow = {
  id: string;
  email: string;
  state: InvitationState;
  expiresAt: string;
  createdAt: string;
};

interface BillingOverview {
  summary: {
    planKey: string | null;
    billingMode: string | null;
    billingStatus: string | null;
    billedSeats: number | null;
    activeMembers: number;
    seatDelta: number | null;
    seatSyncStatus: string;
    requiresReconciliation: boolean;
    renewalDate: number | null;
    isPolarBacked: boolean;
  };
  billing: {
    provider?: string;
  } | null;
}

export const Route = createFileRoute("/organization")({
  component: OrganizationSettingsPage,
});

function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString();
}

function formatPlanKey(value: string | null | undefined) {
  switch (value) {
    case "team_monthly":
      return "Monthly seats";
    case "manual_free":
      return "Manual free";
    case "enterprise_invoice":
      return "Enterprise invoice";
    case "internal":
      return "Internal";
    case "trial":
      return "Trial";
    default:
      return value ?? "Not configured";
  }
}

function formatMode(value: string | null | undefined) {
  if (!value) {
    return "Not configured";
  }
  return value.replace(/_/g, " ");
}

function getSeatSyncLabel(value: string | null | undefined) {
  switch (value) {
    case "in_sync":
      return "In sync";
    case "under_billed":
      return "Under billed";
    case "over_billed":
      return "Over billed";
    case "missing_subscription":
      return "Missing subscription";
    default:
      return "Not applicable";
  }
}

function OrganizationBillingCard(props: {
  billingOverview: BillingOverview | undefined;
  canManageOrg: boolean;
  pendingBillingAction: "checkout" | "portal" | null;
  hasPaidSubscription: boolean;
  hasPolarBilling: boolean;
  onStartSubscription: () => void;
  onManageBilling: () => void;
}) {
  const seatSyncStatus = props.billingOverview?.summary.seatSyncStatus;
  const showSeatDriftWarning = props.billingOverview?.summary.requiresReconciliation;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing</CardTitle>
        <CardDescription>
          Organization billing is provider-backed when Polar is active and entitlement-backed for
          free, internal, trial, and invoice access.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-lg border px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Current plan
            </p>
            <p className="mt-1 text-sm font-medium">
              {formatPlanKey(props.billingOverview?.summary.planKey)}
            </p>
          </div>
          <div className="rounded-lg border px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Billing status
            </p>
            <p className="mt-1 text-sm font-medium">
              {props.billingOverview?.summary.billingStatus ?? "No subscription"}
            </p>
          </div>
          <div className="rounded-lg border px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Billed seats
            </p>
            <p className="mt-1 text-sm font-medium">
              {props.billingOverview?.summary.billedSeats ?? "-"}
            </p>
          </div>
          <div className="rounded-lg border px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Active members
            </p>
            <p className="mt-1 text-sm font-medium">
              {props.billingOverview?.summary.activeMembers ?? "-"}
            </p>
          </div>
          <div className="rounded-lg border px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Seat status
            </p>
            <p className="mt-1 text-sm font-medium">{getSeatSyncLabel(seatSyncStatus)}</p>
          </div>
          <div className="rounded-lg border px-3 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Renewal date
            </p>
            <p className="mt-1 text-sm font-medium">
              {props.billingOverview?.summary.renewalDate
                ? formatDate(new Date(props.billingOverview.summary.renewalDate).toISOString())
                : "-"}
            </p>
          </div>
        </div>

        {!props.billingOverview?.summary.isPolarBacked ? (
          <div className="rounded-lg border border-dashed px-3 py-3 text-sm text-muted-foreground">
            Entitlement mode: {formatMode(props.billingOverview?.summary.billingMode)}
          </div>
        ) : null}

        {props.hasPolarBilling ? (
          <div className="rounded-lg border border-dashed px-3 py-3 text-sm text-muted-foreground">
            Seats are managed automatically from active organization members in the app. Billing
            portal seat assignments are informational only and do not control access.
          </div>
        ) : null}

        {showSeatDriftWarning ? (
          <div className="rounded-lg border px-3 py-3 text-sm text-foreground">
            Billing is currently out of sync with active members by {props.billingOverview?.summary.seatDelta} seat
            {Math.abs(props.billingOverview?.summary.seatDelta ?? 0) === 1 ? "" : "s"}. All users keep
            access while a platform admin reconciles billing.
          </div>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row">
          {!props.hasPaidSubscription ? (
            <Button
              onClick={props.onStartSubscription}
              disabled={!props.canManageOrg || props.pendingBillingAction !== null}
            >
              {props.pendingBillingAction === "checkout" && (
                <Loader2 className="size-4 animate-spin" />
              )}
              Start subscription
            </Button>
          ) : null}
          <Button
            variant="outline"
            onClick={props.onManageBilling}
            disabled={!props.canManageOrg || !props.hasPolarBilling || props.pendingBillingAction !== null}
          >
            {props.pendingBillingAction === "portal" && (
              <Loader2 className="size-4 animate-spin" />
            )}
            Manage billing
          </Button>
        </div>

        {!props.canManageOrg ? (
          <p className="text-sm text-muted-foreground">
            Owners and admins can start subscriptions and open the billing portal.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function AddUserCard(props: {
  inviteEmail: string;
  inviteRole: "member" | "admin";
  isInviting: boolean;
  setInviteEmail: (value: string) => void;
  setInviteRole: (value: "member" | "admin") => void;
  onInvite: (event: React.FormEvent) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Add User</CardTitle>
        <CardDescription>
          Existing WorkOS users without an organization are added immediately. Everyone else
          receives an email invite that lets them sign up and join automatically.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="flex flex-col gap-3 sm:flex-row" onSubmit={props.onInvite}>
          <Input
            type="email"
            placeholder="name@company.com"
            value={props.inviteEmail}
            onChange={(event) => props.setInviteEmail(event.target.value)}
            disabled={props.isInviting}
            autoComplete="off"
          />
          <Select
            value={props.inviteRole}
            onValueChange={(value) => props.setInviteRole(value as "member" | "admin")}
            disabled={props.isInviting}
          >
            <SelectTrigger className="sm:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="member">Member</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={props.isInviting || !props.inviteEmail.trim()}>
            {props.isInviting && <Loader2 className="size-4 animate-spin" />}
            Add User
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PendingInvitationsCard(props: {
  invitations: InvitationRow[] | null;
  invitationsError: string | null;
  pendingInvitationActionIds: Set<string>;
  onResend: (invitationId: string) => void;
  onRevoke: (invitationId: string) => void;
}) {
  const pendingInvitations = props.invitations?.filter((invitation) => invitation.state === "pending") ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Invitations</CardTitle>
      </CardHeader>
      <CardContent>
        {props.invitationsError ? (
          <p className="text-sm text-muted-foreground">{props.invitationsError}</p>
        ) : props.invitations === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading invitations...
          </div>
        ) : pendingInvitations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending invitations.</p>
        ) : (
          <div className="space-y-2">
            {pendingInvitations.map((invitation) => {
              const isWorking = props.pendingInvitationActionIds.has(invitation.id);
              return (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{invitation.email}</p>
                    <p className="text-xs text-muted-foreground">
                      Expires {formatDate(invitation.expiresAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isWorking}
                      onClick={() => props.onResend(invitation.id)}
                    >
                      {isWorking && <Loader2 className="size-3 animate-spin" />}
                      Resend
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isWorking}
                      onClick={() => props.onRevoke(invitation.id)}
                    >
                      {isWorking && <Loader2 className="size-3 animate-spin" />}
                      Revoke
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MembersCard(props: {
  members: Array<{ _id: string; name?: string; email: string; orgRole: string }> | undefined;
  membersStatus: "loading" | "error" | "success";
  membersError: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
      </CardHeader>
      <CardContent>
        {props.membersStatus === "error" ? (
          <p className="text-sm text-muted-foreground">{props.membersError ?? "Failed to load members."}</p>
        ) : props.membersStatus === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading members...
          </div>
        ) : (
          <div className="space-y-2">
            {(props.members ?? []).map((member) => (
              <div
                key={member._id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{member.name?.trim() || member.email}</p>
                  <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                </div>
                <Badge variant="outline" className="uppercase">
                  {member.orgRole}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OrganizationBrandingCard(props: {
  branding:
    | {
        logoUrl?: string | null;
        logoFileKey?: string | null;
        organizationName: string;
      }
    | undefined;
  canManageOrg: boolean;
  organizationName: string | null | undefined;
  isUploadingLogo: boolean;
  isRemovingLogo: boolean;
  onUploadLogo: (file: File | undefined) => void;
  onRemoveLogo: () => void;
}) {
  const displayName = props.branding?.organizationName ?? props.organizationName ?? "Organization";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Organization Branding</CardTitle>
        <CardDescription>
          This logo appears in the application and generated report headers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex size-20 items-center justify-center rounded-md border-2 bg-muted/40">
            {props.branding?.logoUrl ? (
              <img
                src={props.branding.logoUrl}
                alt={`${displayName} logo`}
                className="max-h-16 max-w-16 object-contain"
              />
            ) : (
              <span className="text-xs font-semibold uppercase text-muted-foreground">No Logo</span>
            )}
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium">{displayName}</p>
            <p className="text-sm text-muted-foreground">
              Use a PNG, JPG, or SVG logo up to 5 MB.
            </p>
          </div>
          {props.canManageOrg ? (
            <div className="flex flex-wrap gap-2">
              <Button asChild variant="outline" disabled={props.isUploadingLogo || props.isRemovingLogo}>
                <label className="cursor-pointer">
                  {props.isUploadingLogo ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Upload className="size-4" />
                  )}
                  Upload Logo
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(event) => {
                      props.onUploadLogo(event.currentTarget.files?.[0]);
                      event.currentTarget.value = "";
                    }}
                  />
                </label>
              </Button>
              {props.branding?.logoFileKey ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={props.onRemoveLogo}
                  disabled={props.isUploadingLogo || props.isRemovingLogo}
                >
                  {props.isRemovingLogo ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <X className="size-4" />
                  )}
                  Remove
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export function OrganizationSettingsPage() {
  const { userProfile: viewer } = useUserProfile();
  const {
    data: billingOverview,
    isPending: isBillingOverviewPending,
    error: billingOverviewError,
  } = useQuery(
    convexQuery(
      api.billing.queries.getOrganizationBillingOverview,
      viewer?.organizationId ? {} : "skip"
    )
  );
  const {
    data: members,
    isPending: isMembersPending,
    error: membersError,
  } = useQuery(
    convexQuery(
      api.auth.listOrganizationMembers,
      viewer?.orgRole === "owner" || viewer?.orgRole === "admin" ? {} : "skip"
    )
  );
  const { data: branding } = useQuery(
    convexQuery(api.organizations.getBranding, viewer?.organizationId ? {} : "skip")
  );

  const generateR2UploadUrl = useMutation(api.files.generateR2UploadUrl);
  const updateBranding = useMutation(api.organizations.updateBranding);
  const removeLogo = useMutation(api.organizations.removeLogo);
  const listInvitations = useAction(api.authActions.listOrganizationInvitations);
  const inviteMember = useAction(api.authActions.inviteOrganizationMember);
  const resendInvitation = useAction(api.authActions.resendOrganizationInvitation);
  const revokeInvitation = useAction(api.authActions.revokeOrganizationInvitation);
  const startSubscription = useAction(api.billing.actions.startSubscription);
  const manageBilling = useAction(api.billing.actions.manageBilling);

  const canManageOrg = viewer?.orgRole === "owner" || viewer?.orgRole === "admin";
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "admin">("member");
  const [isInviting, setIsInviting] = useState(false);
  const [pendingBillingAction, setPendingBillingAction] = useState<
    "checkout" | "portal" | null
  >(null);
  const [invitations, setInvitations] = useState<InvitationRow[] | null>(null);
  const [invitationsError, setInvitationsError] = useState<string | null>(null);
  const [pendingInvitationActionIds, setPendingInvitationActionIds] = useState<
    Set<string>
  >(new Set());
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [isRemovingLogo, setIsRemovingLogo] = useState(false);

  const refreshInvitations = useCallback(async () => {
    if (!canManageOrg) {
      setInvitations(null);
      setInvitationsError(null);
      return;
    }
    try {
      const result = await listInvitations({});
      setInvitations(result);
      setInvitationsError(null);
    } catch (error) {
      console.error("List invitations error:", error);
      setInvitations([]);
      setInvitationsError("Failed to load invitations");
      toast.error("Failed to load invitations");
    }
  }, [canManageOrg, listInvitations]);

  const membersStatus: "loading" | "error" | "success" =
    isMembersPending ? "loading" : membersError ? "error" : "success";

  useEffect(() => {
    void refreshInvitations();
  }, [refreshInvitations]);

  const hasPolarBilling =
    billingOverview !== undefined &&
    (billingOverview.summary.isPolarBacked || billingOverview.billing?.provider === "polar");
  const hasPaidSubscription =
    billingOverview !== undefined &&
    hasPolarBilling &&
    ["active", "trialing", "past_due", "unpaid"].includes(
      billingOverview.summary.billingStatus ?? ""
    );
  const handleStartSubscription = async () => {
    setPendingBillingAction("checkout");
    try {
      const origin = window.location.origin;
      const result = await startSubscription({
        origin,
        successUrl: `${origin}/organization`,
        returnUrl: `${origin}/organization`,
      });
      window.location.href = result.url;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to start subscription"
      );
      console.error("Start subscription error:", error);
      setPendingBillingAction(null);
    }
  };

  const handleManageBilling = async () => {
    setPendingBillingAction("portal");
    try {
      const result = await manageBilling({ returnUrl: `${window.location.origin}/organization` });
      window.location.href = result.url;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to open billing portal");
      console.error("Manage billing error:", error);
      setPendingBillingAction(null);
    }
  };

  const handleInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = inviteEmail.trim();
    if (!email) {
      return;
    }

    setIsInviting(true);
    try {
      const result = await inviteMember({ email, role: inviteRole });
      setInviteEmail("");
      toast.success(
        result.mode === "added"
          ? "User added to the organization"
          : "Invitation sent"
      );
      await refreshInvitations();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to add user");
      console.error("Invite error:", error);
    } finally {
      setIsInviting(false);
    }
  };

  const handleLogoUpload = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Upload an image file for the organization logo");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo must be 5 MB or smaller");
      return;
    }

    setIsUploadingLogo(true);
    try {
      const { key, url } = await generateR2UploadUrl({
        prefix: "organization-branding/logos",
        filename: file.name,
      });
      const response = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!response.ok) {
        throw new Error("Logo upload failed");
      }
      await updateBranding({ logoFileKey: key, logoMimeType: file.type });
      toast.success("Organization logo updated");
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to update logo");
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setIsRemovingLogo(true);
    try {
      await removeLogo({});
      toast.success("Organization logo removed");
    } catch (error) {
      console.error("Logo remove error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to remove logo");
    } finally {
      setIsRemovingLogo(false);
    }
  };

  const handleResend = async (invitationId: string) => {
    setPendingInvitationActionIds((current) => {
      const next = new Set(current);
      next.add(invitationId);
      return next;
    });
    try {
      await resendInvitation({ invitationId });
      toast.success("Invitation resent");
      await refreshInvitations();
    } catch (error) {
      toast.error("Failed to resend invitation");
      console.error("Resend invitation error:", error);
    } finally {
      setPendingInvitationActionIds((current) => {
        const next = new Set(current);
        next.delete(invitationId);
        return next;
      });
    }
  };

  const handleRevoke = async (invitationId: string) => {
    setPendingInvitationActionIds((current) => {
      const next = new Set(current);
      next.add(invitationId);
      return next;
    });
    try {
      await revokeInvitation({ invitationId });
      toast.success("Invitation revoked");
      await refreshInvitations();
    } catch (error) {
      toast.error("Failed to revoke invitation");
      console.error("Revoke invitation error:", error);
    } finally {
      setPendingInvitationActionIds((current) => {
        const next = new Set(current);
        next.delete(invitationId);
        return next;
      });
    }
  };

  if (viewer === null) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organization"
        description="Manage your organization settings, members, and invites."
      />

      <Card>
        <CardHeader>
          <CardTitle>Active Organization</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">Name:</span>{" "}
            {viewer?.organizationName ?? "Unknown"}
          </p>
          <p>
            <span className="text-muted-foreground">Role:</span>{" "}
            <Badge variant="outline" className="uppercase">
              {viewer?.orgRole ?? "member"}
            </Badge>
          </p>
        </CardContent>
      </Card>

      <OrganizationBrandingCard
        branding={branding}
        canManageOrg={canManageOrg}
        organizationName={viewer?.organizationName}
        isUploadingLogo={isUploadingLogo}
        isRemovingLogo={isRemovingLogo}
        onUploadLogo={(file) => void handleLogoUpload(file)}
        onRemoveLogo={() => void handleRemoveLogo()}
      />

      {isBillingOverviewPending ? (
        <Card>
          <CardContent className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Loading billing...
          </CardContent>
        </Card>
      ) : billingOverviewError ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Failed to load billing.
          </CardContent>
        </Card>
      ) : (
        <OrganizationBillingCard
          billingOverview={billingOverview as BillingOverview}
          canManageOrg={canManageOrg}
          pendingBillingAction={pendingBillingAction}
          hasPaidSubscription={hasPaidSubscription}
          hasPolarBilling={hasPolarBilling}
          onStartSubscription={() => void handleStartSubscription()}
          onManageBilling={() => void handleManageBilling()}
        />
      )}

      {!canManageOrg ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Owners and admins can manage invitations and members.
          </CardContent>
        </Card>
      ) : (
        <>
          <AddUserCard
            inviteEmail={inviteEmail}
            inviteRole={inviteRole}
            isInviting={isInviting}
            setInviteEmail={setInviteEmail}
            setInviteRole={setInviteRole}
            onInvite={handleInvite}
          />

          <PendingInvitationsCard
            invitations={invitations}
            invitationsError={invitationsError}
            pendingInvitationActionIds={pendingInvitationActionIds}
            onResend={(invitationId) => void handleResend(invitationId)}
            onRevoke={(invitationId) => void handleRevoke(invitationId)}
          />

          <MembersCard
            members={members as Array<{ _id: string; name?: string; email: string; orgRole: string }> | undefined}
            membersStatus={membersStatus}
            membersError={membersError instanceof Error ? membersError.message : null}
          />
        </>
      )}
    </div>
  );
}
